import { CommonModule } from '@angular/common';
import { Component, NgZone, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { getCallbackNamespace } from 'auth-service-lib';
import { ErrorSeverity, MessageNotificationService } from 'message-notification-service-lib';
import { SharedServiceProviderServiceFactory } from 'shared-service-provider-lib';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.scss'],
  imports: [CommonModule, MatIconModule],
})
export class AuthCallbackComponent implements OnInit {
  authStatus = { icon: 'app-loading', text: 'Completing sign-in...' };

  constructor(
    private serviceProviderFactory: SharedServiceProviderServiceFactory,
    private router: Router,
    private notificationService: MessageNotificationService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    setTimeout(() => this.handleCallback());
  }

  private updateAuthStatus(icon: string, text: string): void {
    this.authStatus = { icon, text };
  }

  private handleCallback(): void {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('error')) {
      this.ensureBackGoesToSignIn();
      this.updateAuthStatus('broken_image', urlParams.get('error') ?? 'Unknown error');
      return;
    }

    try {
      const namespace = getCallbackNamespace(window.location.href);
      if (!namespace) {
        throw new Error('Could not determine auth namespace from callback URL');
      }
      localStorage.setItem('lastLoginNamespace', namespace);
      const authService = this.serviceProviderFactory.getServiceProviderService(namespace).getAuthService();
      const returnUrl = authService.finishAuthFlow(window.location.href);
      const parsed = new URL(returnUrl);
      const queryParams: Record<string, string> = {};
      parsed.searchParams.forEach((value, key) => { queryParams[key] = value; });

      const destination = parsed.pathname === '/signin' ? '/' : parsed.pathname;
      const destinationQueryParams = parsed.pathname === '/signin' ? {} : queryParams;
      this.ngZone.runOutsideAngular(() => {
        window.history.replaceState(null, '', '/signin');
      });
      this.router.navigate([destination], { queryParams: destinationQueryParams });
    } catch (err) {
      const errorMsg = err instanceof Error
        ? err.message
        : 'An error occurred during authentication. Please refresh and try again.';
      this.ensureBackGoesToSignIn();
      this.updateAuthStatus('error', errorMsg);
      this.notificationService.error('Authentication failed', err, undefined, ErrorSeverity.CRITICAL);
    }
  }

  private ensureBackGoesToSignIn(): void {
    const callbackUrl = window.location.pathname + window.location.search;
    this.ngZone.runOutsideAngular(() => {
      window.history.replaceState(null, '', '/signin');
      window.history.pushState(null, '', callbackUrl);
    });
  }
}
